import { useRef, useState } from 'react';
import * as filesApi from '../api/files';
import { usePagedList } from '../lib/usePagedList';
import { useConfirm } from '../lib/confirm';
import { useToast } from '../components/toast';
import { ErrorAlert, Spinner, EmptyState, PagedListFooter, formatDate } from '../components/ui';
import { cn, ui } from '../lib/ui';

const PAGE_SIZE = 25;

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Files() {
  const confirm = useConfirm();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null); // fileId currently downloading/deleting
  const inputRef = useRef(null);

  const list = usePagedList(
    cursor =>
      filesApi
        .listFiles({ limit: PAGE_SIZE, lastEvaluatedKey: cursor })
        .then(d => ({ items: d.files || [], lastEvaluatedKey: d.lastEvaluatedKey })),
    []
  );

  async function onUpload(event) {
    const file = event.target.files[0];
    if (!file) return;
    setBusy(true);
    try {
      await filesApi.uploadFile(file);
      toast.success(`Uploaded "${file.name}".`);
      list.reload();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function onDownload(fileId) {
    setBusyId(fileId);
    try {
      const { downloadUrl } = await filesApi.getDownloadUrl(fileId);
      window.open(downloadUrl, '_blank', 'noopener');
    } catch (err) {
      // Freshly uploaded files stay blocked until the malware scan clears
      // them (fail-closed server policy) — surface the API's message as-is.
      toast.error(err);
    } finally {
      setBusyId(null);
    }
  }

  async function onDelete(file) {
    const ok = await confirm({
      title: 'Delete document?',
      message: `Delete "${file.fileName}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    setBusyId(file.fileId);
    try {
      await filesApi.deleteFile(file.fileId);
      list.removeItem(file.fileId, 'fileId');
      toast.success(`Deleted "${file.fileName}".`);
    } catch (err) {
      toast.error(err);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <div className={cn(ui.row, ui.spread, 'mb-4')}>
        <h1 className="text-title-l">Documents</h1>
        <label className={ui.btnPrimary}>
          {busy ? 'Uploading…' : 'Upload file'}
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            disabled={busy}
            onChange={onUpload}
          />
        </label>
      </div>
      <ErrorAlert error={list.error} />
      {list.loading ? (
        <Spinner />
      ) : list.items.length === 0 ? (
        !list.error && <EmptyState>No documents uploaded yet.</EmptyState>
      ) : (
        <div className={cn(ui.card, ui.tableWrap)}>
          <table className={ui.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Size</th>
                <th>Uploaded</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.items.map(f => (
                <tr key={f.fileId}>
                  <td>{f.fileName}</td>
                  <td>{formatSize(f.fileSize)}</td>
                  <td>{formatDate(f.uploadedAt || f.createdAt)}</td>
                  <td className={ui.row}>
                    <button
                      className={ui.btnSecondary}
                      disabled={busyId === f.fileId}
                      onClick={() => onDownload(f.fileId)}
                    >
                      Download
                    </button>
                    <button
                      className={ui.btnDanger}
                      disabled={busyId === f.fileId}
                      onClick={() => onDelete(f)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <PagedListFooter
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        endLabel={list.items.length ? 'No more documents.' : null}
      />
    </>
  );
}
